import {
  wordDescriptionGenres,
  type CustomWordDescriptionItem,
  type WordDescriptionGenreId,
} from "../data/wordDescriptionItems";

const STORAGE_KEY = "randombomb:word-description:v1";
const DEFAULT_GENRE_ID = wordDescriptionGenres[0].id;
const DEFAULT_ROUND_SIZE = 12;

export type WordDescriptionStorageState = {
  selectedGenreId: WordDescriptionGenreId;
  customWords: CustomWordDescriptionItem[];
  roundSize: number;
  remainingItemIdsByGenre: Record<WordDescriptionGenreId, string[]>;
};

const createEmptyRemainingItemIds = (): Record<
  WordDescriptionGenreId,
  string[]
> => ({
  daily: [],
  food: [],
  nature: [],
  culture: [],
  custom: [],
});

const createDefaultStorageState = (): WordDescriptionStorageState => ({
  selectedGenreId: DEFAULT_GENRE_ID,
  customWords: [],
  roundSize: DEFAULT_ROUND_SIZE,
  remainingItemIdsByGenre: createEmptyRemainingItemIds(),
});

const isGenreId = (value: unknown): value is WordDescriptionGenreId =>
  wordDescriptionGenres.some((genre) => genre.id === value);

const sanitizeCustomWords = (value: unknown): CustomWordDescriptionItem[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (!item || typeof item !== "object") return [];

    const nextItem = item as Partial<CustomWordDescriptionItem>;
    if (
      typeof nextItem.id !== "string" ||
      !isGenreId(nextItem.genreId) ||
      typeof nextItem.word !== "string" ||
      typeof nextItem.createdAt !== "number" ||
      nextItem.word.trim().length === 0
    ) {
      return [];
    }

    return [{
      id: nextItem.id,
      genreId: nextItem.genreId,
      word: nextItem.word.trim(),
      createdAt: nextItem.createdAt,
    }];
  });
};

const sanitizeRoundSize = (value: unknown) => {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return DEFAULT_ROUND_SIZE;
  }

  return Math.min(Math.max(value, 1), 36);
};

const sanitizeRemainingItemIds = (
  value: unknown,
): Record<WordDescriptionGenreId, string[]> => {
  const emptyState = createEmptyRemainingItemIds();
  if (!value || typeof value !== "object") return emptyState;

  const storedState = value as Partial<
    Record<WordDescriptionGenreId, unknown>
  >;

  wordDescriptionGenres.forEach((genre) => {
    const storedIds = storedState[genre.id];
    emptyState[genre.id] = Array.isArray(storedIds)
      ? Array.from(
          new Set(
            storedIds.filter(
              (itemId): itemId is string => typeof itemId === "string",
            ),
          ),
        )
      : [];
  });

  return emptyState;
};

export const loadWordDescriptionStorage = (): WordDescriptionStorageState => {
  if (typeof window === "undefined") {
    return createDefaultStorageState();
  }

  try {
    const rawStorage = window.localStorage.getItem(STORAGE_KEY);
    if (!rawStorage) {
      return createDefaultStorageState();
    }

    const parsedStorage = JSON.parse(rawStorage) as Partial<WordDescriptionStorageState>;
    return {
      selectedGenreId: isGenreId(parsedStorage.selectedGenreId)
        ? parsedStorage.selectedGenreId
        : DEFAULT_GENRE_ID,
      customWords: sanitizeCustomWords(parsedStorage.customWords),
      roundSize: sanitizeRoundSize(parsedStorage.roundSize),
      remainingItemIdsByGenre: sanitizeRemainingItemIds(
        parsedStorage.remainingItemIdsByGenre,
      ),
    };
  } catch {
    return createDefaultStorageState();
  }
};

export const saveWordDescriptionStorage = (
  state: WordDescriptionStorageState,
) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage를 사용할 수 없는 환경에서는 현재 세션에서만 유지합니다.
  }
};
